import { useState } from "react"
import { Check, ChevronDown } from "lucide-react"

import { Button, cn } from "@aix/ui"

// Camelot wheel keys - 24 keys total (12 minor A, 12 major B)
const CAMELOT_KEYS = [
  // Inner ring (minor keys - A)
  { code: "1A", name: "A♭m", color: "bg-purple-200 dark:bg-purple-900" },
  { code: "2A", name: "E♭m", color: "bg-violet-200 dark:bg-violet-900" },
  { code: "3A", name: "B♭m", color: "bg-indigo-200 dark:bg-indigo-900" },
  { code: "4A", name: "Fm", color: "bg-blue-200 dark:bg-blue-900" },
  { code: "5A", name: "Cm", color: "bg-cyan-200 dark:bg-cyan-900" },
  { code: "6A", name: "Gm", color: "bg-teal-200 dark:bg-teal-900" },
  { code: "7A", name: "Dm", color: "bg-green-200 dark:bg-green-900" },
  { code: "8A", name: "Am", color: "bg-lime-200 dark:bg-lime-900" },
  { code: "9A", name: "Em", color: "bg-yellow-200 dark:bg-yellow-900" },
  { code: "10A", name: "Bm", color: "bg-amber-200 dark:bg-amber-900" },
  { code: "11A", name: "F♯m", color: "bg-orange-200 dark:bg-orange-900" },
  { code: "12A", name: "C♯m", color: "bg-red-200 dark:bg-red-900" },
  // Outer ring (major keys - B)
  { code: "1B", name: "B", color: "bg-purple-400 dark:bg-purple-700" },
  { code: "2B", name: "G♭", color: "bg-violet-400 dark:bg-violet-700" },
  { code: "3B", name: "D♭", color: "bg-indigo-400 dark:bg-indigo-700" },
  { code: "4B", name: "A♭", color: "bg-blue-400 dark:bg-blue-700" },
  { code: "5B", name: "E♭", color: "bg-cyan-400 dark:bg-cyan-700" },
  { code: "6B", name: "B♭", color: "bg-teal-400 dark:bg-teal-700" },
  { code: "7B", name: "F", color: "bg-green-400 dark:bg-green-700" },
  { code: "8B", name: "C", color: "bg-lime-400 dark:bg-lime-700" },
  { code: "9B", name: "G", color: "bg-yellow-400 dark:bg-yellow-700" },
  { code: "10B", name: "D", color: "bg-amber-400 dark:bg-amber-700" },
  { code: "11B", name: "A", color: "bg-orange-400 dark:bg-orange-700" },
  { code: "12B", name: "E", color: "bg-red-400 dark:bg-red-700" },
] as const

interface CamelotKeySelectorProps {
  value: string | null
  onChange: (key: string | null) => void
  disabled?: boolean
  compact?: boolean
}

export function CamelotKeySelector({ value, onChange, disabled = false, compact = false }: CamelotKeySelectorProps) {
  const [isOpen, setIsOpen] = useState(false)

  const selectedKey = CAMELOT_KEYS.find((k) => k.code === value)

  const handleSelect = (code: string) => {
    onChange(code === value ? null : code)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={cn("w-full justify-between", compact && "h-7 text-xs px-2", selectedKey && selectedKey.color)}
      >
        <span className={cn(selectedKey && "font-medium")}>
          {selectedKey ? `${selectedKey.code} (${selectedKey.name})` : "Set Key"}
        </span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div className="absolute left-0 top-full mt-1 z-50 bg-popover border border-border rounded-lg shadow-lg p-3 min-w-64">
            <div className="text-xs font-medium text-muted-foreground mb-2">Minor Keys (Inner Wheel)</div>
            <div className="grid grid-cols-6 gap-1 mb-3">
              {CAMELOT_KEYS.slice(0, 12).map((key) => (
                <button
                  key={key.code}
                  onClick={() => handleSelect(key.code)}
                  className={cn(
                    "p-1.5 rounded text-xs font-medium transition-all",
                    key.color,
                    "hover:ring-2 hover:ring-primary hover:ring-offset-1",
                    value === key.code && "ring-2 ring-primary ring-offset-1",
                  )}
                  title={`${key.code} - ${key.name}`}
                >
                  {key.code}
                  {value === key.code && <Check className="h-3 w-3 inline ml-0.5" />}
                </button>
              ))}
            </div>

            <div className="text-xs font-medium text-muted-foreground mb-2">Major Keys (Outer Wheel)</div>
            <div className="grid grid-cols-6 gap-1 mb-3">
              {CAMELOT_KEYS.slice(12).map((key) => (
                <button
                  key={key.code}
                  onClick={() => handleSelect(key.code)}
                  className={cn(
                    "p-1.5 rounded text-xs font-medium transition-all",
                    key.color,
                    "hover:ring-2 hover:ring-primary hover:ring-offset-1",
                    value === key.code && "ring-2 ring-primary ring-offset-1",
                  )}
                  title={`${key.code} - ${key.name}`}
                >
                  {key.code}
                  {value === key.code && <Check className="h-3 w-3 inline ml-0.5" />}
                </button>
              ))}
            </div>

            {value && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground"
                onClick={() => handleSelect(value)}
              >
                Clear Selection
              </Button>
            )}

            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
              <p>
                <strong>Tip:</strong> Keys with the same number mix well.
              </p>
              <p>Adjacent numbers (±1) also blend smoothly.</p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Helper to get compatible keys for a given Camelot key
